import { formatMoney, currencySymbol } from "aldoo-ra/Store/currency"

// the currency the figures' money is in: the store's, as the dashboards'
// catalog says (aldoo-na/store/currency.js, STORE_CURRENCY)
let moneyCurrency = null
export const setMoneyCurrency = (currency) => {
  if (currency) moneyCurrency = currency
}
/** The symbol the figures' money is written with ("$", "€" …), empty before the catalog answered. */
export const moneySymbol = () => currencySymbol(moneyCurrency)

// How a figure reads: money in cents of the store's currency, a percent, a count
export const formatValue = (value, unit, { compact = false } = {}) => {
  const n = Number(value) || 0
  if (unit === "money") return formatMoney(n, moneyCurrency, { cents: true, compact })
  if (unit === "percent") return `${n.toFixed(n >= 10 ? 1 : 2)}%`
  if (unit === "number") return n.toLocaleString(undefined, { maximumFractionDigits: 2 })
  return Math.round(n).toLocaleString()
}

/**
 * The change against the compared period as the analytics dashboards show
 * it: "+12.4%" in green when it went up, "-8%" in red when it went down.
 * Growing from nothing reads +100%; nothing to nothing, 0%.
 */
export const formatChange = (value, previous) => {
  // nothing to compare with (a timeless figure, no comparison): no change to show
  if (previous === null || previous === undefined) return null
  const now = Number(value) || 0
  const before = Number(previous) || 0
  const change = before ? ((now - before) / Math.abs(before)) * 100 : now ? (now > 0 ? 100 : -100) : 0
  const digits = Math.abs(change) >= 10 ? 1 : Math.abs(change) >= 1 ? 1 : 2
  return {
    text: `${change > 0 ? "+" : change < 0 ? "−" : ""}${Math.abs(change).toFixed(change === 0 ? 0 : digits)}%`,
    className: change > 0 ? "text-admin_success" : change < 0 ? "text-admin_error" : "text-admin_text_secondary",
  }
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

/** A bucket key as a chart axis reads it. */
export const formatKey = (key, granularity) => {
  if (!key) return ""
  if (granularity === "hour") return key.slice(11, 16)
  if (granularity === "month") return `${MONTHS[Number(key.slice(5, 7)) - 1]} ${key.slice(2, 4)}`
  return `${Number(key.slice(8, 10))} ${MONTHS[Number(key.slice(5, 7)) - 1]}`
}

const DAY_MS = 24 * 60 * 60 * 1000
const utc = (date) => Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10)))
const isoDay = (ms) => new Date(ms).toISOString().slice(0, 10)
const dayMonth = (date) => `${Number(date.slice(8, 10))} ${MONTHS[Number(date.slice(5, 7)) - 1]}`

/** "28–29 Sep", "29 Sep – 4 Oct", or one day. */
const dayRange = (from, to) => {
  if (from === to) return dayMonth(from)
  if (from.slice(0, 7) === to.slice(0, 7)) return `${Number(from.slice(8, 10))}–${dayMonth(to)}`
  return `${dayMonth(from)} – ${dayMonth(to)}`
}

/**
 * The days a bucket covers within the period: a week (keyed by its Monday)
 * or a month cut at the period's ends — the first week of "This year" starts
 * on 1 Jan, not on the Monday before it, and the last one ends today.
 */
export const bucketDays = (key, granularity, period) => {
  if (!key || !period || (granularity !== "week" && granularity !== "month")) return null
  const start = granularity === "week" ? key : `${key}-01`
  const end = granularity === "week" ? isoDay(utc(key) + 6 * DAY_MS) : isoDay(Date.UTC(Number(key.slice(0, 4)), Number(key.slice(5, 7)), 0))
  return { from: start < period.from ? period.from : start, to: end > period.to ? period.to : end, clipped: start < period.from || end > period.to }
}

/**
 * How a bucket reads. On the axis (`full` false) a week is its first day in
 * the period, and the last week — cut short by the period's end — says where
 * it ends ("28–29 Sep"); in a tooltip (`full`) a week is all its days, and a
 * month cut short says which of its days are in.
 */
export const formatBucket = (key, granularity, period, { full = false, last = false } = {}) => {
  const days = bucketDays(key, granularity, period)
  if (!days) return formatKey(key, granularity)
  if (granularity === "month") return full && days.clipped ? `${dayRange(days.from, days.to)} ${days.to.slice(0, 4)}` : formatKey(key, granularity)
  if (full || (last && days.clipped)) return dayRange(days.from, days.to)
  return dayMonth(days.from)
}

/** "1 Sep – 30 Sep 2026" */
export const formatPeriod = (period) => {
  if (!period) return ""
  const part = (date, withYear) => `${Number(date.slice(8, 10))} ${MONTHS[Number(date.slice(5, 7)) - 1]}${withYear ? ` ${date.slice(0, 4)}` : ""}`
  if (period.from === period.to) return part(period.from, true)
  return `${part(period.from, period.from.slice(0, 4) !== period.to.slice(0, 4))} – ${part(period.to, true)}`
}
